(() => {
  const P = () => window.GhaliaPricing;
  const moneyNet = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);
  const moneyDisplay = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);

  const readThemeConfig = (drawer) => ({
    enabled: drawer?.dataset.globalDiscountEnabled === 'true',
    pct: Number(drawer?.dataset.globalDiscountPct || 0),
    maxNet: Number(drawer?.dataset.globalDiscountMaxNet || 0),
    label: drawer?.dataset.globalDiscountLabel || 'Extra savings',
  });

  const vatRateFromDrawer = (drawer) =>
    Number(drawer?.querySelector('[data-cart-summary]')?.dataset.vatRate || 15);

  /** Theme SAR settings are VAT-inclusive customer amounts → ex-VAT cents for cart math. */
  const sarGrossToNetCents = (sar, drawer) => {
    if (!sar) return 0;
    const vatRate = vatRateFromDrawer(drawer);
    return Math.round((sar * 100 * 100) / (100 + vatRate));
  };

  const promoMaxNetCents = (drawer) => sarGrossToNetCents(Number(drawer?.dataset.promoMaxSar || 0), drawer);

  const promoMinNetCents = (drawer) => {
    const sar = Number(drawer?.dataset.promoMinSar || drawer?.dataset.promoBasketCapSar || 0);
    return sarGrossToNetCents(sar, drawer);
  };

  const qualifyingMeetsPromoMinimum = (qualifyingNet, drawer) => {
    const minNet = promoMinNetCents(drawer);
    if (minNet <= 0) return true;
    return qualifyingNet >= minNet;
  };

  const capPromoSavings = (savings, drawer) => {
    const maxNet = promoMaxNetCents(drawer);
    if (maxNet > 0 && savings > maxNet) return maxNet;
    return savings;
  };

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const storedPromoCode = () => {
    try {
      return sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const promoTitleFromLines = (cart) => {
    for (const item of cart?.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const title = alloc?.discount_application?.title;
        if (title) return String(title).trim();
      }
    }
    return '';
  };

  const inferredPromoSavingsNet = (cart, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return 0;
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isNativeBundleLine(item) || pricing.isFreeGiftLine(item)) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      const domNet = Number(row?.dataset?.cartLineNet || 0);
      const originalNet =
        domNet > 0 ? domNet : Number(item.original_line_price) || Number(item.line_price) || 0;
      const finalNet = Number(item.final_line_price) || 0;
      if (originalNet > finalNet) sum += originalNet - finalNet;
    });
    return sum;
  };

  const promoCodeApplication = (cart, drawer) => {
    const apps = cart?.cart_level_discount_applications || [];
    const fromCart = apps.find((d) => d?.type === 'discount_code') || apps[0];
    if (fromCart?.title) return fromCart;

    const lineTitle = promoTitleFromLines(cart);
    const fromLines = linePromoAllocationNet(cart);
    const inferred = drawer ? inferredPromoSavingsNet(cart, drawer) : 0;
    const savings = fromLines > 0 ? fromLines : inferred;
    const stored = storedPromoCode();
    const title = lineTitle || stored;

    if (!title) return null;

    const totalDiscount = Number(cart?.total_discount) || 0;
    const allocated = savings > 0 ? savings : totalDiscount;
    return {
      type: 'discount_code',
      title,
      total_allocated_amount: allocated,
      value_type: 'percentage',
      value: 10,
    };
  };

  const promoPercent = (promo) => {
    const raw = Number(promo?.value) || 0;
    if (promo?.value_type !== 'percentage') return 0;
    return raw <= 1 ? raw * 100 : raw;
  };

  const linePromoAllocationNet = (cart) => {
    const pricing = P();
    return (cart?.items || []).reduce((sum, item) => {
      if (pricing?.isNativeBundleLine?.(item) || pricing?.isFreeGiftLine?.(item)) return sum;
      return (
        sum +
        (item.line_level_discount_allocations || []).reduce(
          (lineSum, alloc) => lineSum + (Number(alloc.amount) || 0),
          0
        )
      );
    }, 0);
  };

  /** Catalog ex-VAT subtotal (pre promo) — mirrors cart-line-net-cents / data-cart-line-net. */
  const catalogNetSubtotal = (cart, drawer, pricing) => {
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const catalog =
        Number(row?.dataset?.cartLineNet || 0) ||
        Number(item.original_line_price) ||
        Number(item.original_price ?? item.price ?? 0) * (Number(item.quantity) || 1);
      sum += catalog;
    });
    return sum;
  };

  /** Shopify code promos update line final_line_price — do not subtract promo row twice. */
  const promoAlreadyInSubtotal = (cart, drawer, pricing, promoSavings, netSubtotal) => {
    if (promoSavings <= 0) return false;
    if (linePromoAllocationNet(cart) > 0) return true;
    const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
    if (catalogNet > netSubtotal && catalogNet - netSubtotal >= promoSavings - 1) return true;
    const inferred = inferredPromoSavingsNet(cart, drawer);
    return inferred > 0 && Math.abs(inferred - promoSavings) <= 1;
  };

  const computePromoSavings = (cart, drawer, stackState, cfg) => {
    const pricing = P();
    if (!pricing) return { savings: 0, label: '', pct: 0, isCode: false };

    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    if (qualifyingNet <= 0) return { savings: 0, label: '', pct: 0, isCode: false };
    if (!qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) {
      return { savings: 0, label: '', pct: 0, isCode: false };
    }

    const promo = promoCodeApplication(cart, drawer);
    if (promo) {
      const fromLines = linePromoAllocationNet(cart);
      const fromCart = Number(promo.total_allocated_amount || 0);
      const pct = promoPercent(promo);
      let savings = fromLines > 0 ? fromLines : fromCart;
      const totalDiscount = Number(cart?.total_discount) || 0;
      if (savings <= 0 && totalDiscount > 0) savings = totalDiscount;

      if (savings <= 0 && promo.value_type === 'percentage' && pct > 0) {
        savings = Math.round((qualifyingNet * pct) / 100);
      }
      if (savings <= 0 && promo.value_type !== 'percentage') {
        const fixedNet = Number(promo.total_allocated_amount || 0);
        savings = fixedNet > 0 ? Math.min(qualifyingNet, fixedNet) : 0;
      }
      if (savings > 0) {
        savings = capPromoSavings(savings, drawer);
        return { savings, label: promo.title || 'Promo', pct, isCode: true };
      }
    }

    if (cfg.enabled && cfg.pct > 0) {
      let savings = Math.round((qualifyingNet * cfg.pct) / 100);
      if (cfg.maxNet > 0) savings = Math.min(savings, cfg.maxNet);
      return { savings, label: cfg.label, pct: cfg.pct, isCode: false };
    }

    const inferred = inferredPromoSavingsNet(cart, drawer);
    const code = promoTitleFromLines(cart) || storedPromoCode();
    if (inferred > 0 && code) {
      return { savings: capPromoSavings(inferred, drawer), label: code, pct: 10, isCode: true };
    }

    if (code) {
      const savings = capPromoSavings(Math.round((qualifyingNet * 10) / 100), drawer);
      if (savings > 0) {
        return { savings, label: code, pct: 10, isCode: true };
      }
    }

    return { savings: 0, label: '', pct: 0, isCode: false };
  };

  const lineUnitNet = (item, row, pricing) => {
    const qty = Number(item.quantity) || 1;
    const { net, unitNet } = pricing.cartLineAmounts(item, row);
    if (unitNet > 0) return unitNet;
    return Math.round(net / qty);
  };

  const lineOriginalNet = (item, row) => {
    const qty = Number(item.quantity) || 1;
    const domNet = Number(row?.dataset?.cartLineNet || 0);
    if (domNet > 0) return domNet;
    const origLine = Number(item.original_line_price) || 0;
    if (origLine > 0) return origLine;
    const unit = Number(item.original_price ?? item.price ?? 0);
    if (unit > 0) return unit * qty;
    return Number(item.line_price) || 0;
  };

  const showPromoLinePrice = (priceEl, compareEl, currentEl, pricing, originalNet, finalNet) => {
    if (!currentEl || originalNet <= 0) return;
    currentEl.textContent = pricing.formatNet(finalNet);
    if (compareEl && originalNet > finalNet) {
      compareEl.textContent = pricing.formatNet(originalNet);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
    } else if (compareEl) {
      compareEl.hidden = true;
    }
    priceEl?.classList.add('is-promo-discounted');
  };

  const bundleAndPromoUnits = (item, stackState) => {
    const pricing = P();
    const qty = Number(item.quantity) || 1;
    if (pricing?.isNativeBundleLine?.(item)) {
      return { bundleUnits: qty, promoUnits: 0, qty };
    }
    const fromLine = stackState?.bundleUnitsByLine?.get(item.key);
    const bundleUnits =
      stackState?.memberSet?.has(item.handle) && fromLine > 0
        ? fromLine
        : 0;
    const promoUnits = Math.max(0, qty - bundleUnits);
    return { bundleUnits, promoUnits, qty };
  };

  const applyPromoLinePrices = (cart, drawer, stackState, promoSavings, qualifyingNet) => {
    const pricing = P();
    if (!pricing || !drawer) return;
    if (!qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) {
      (cart?.items || []).forEach((item) => {
        if (pricing.isFreeGiftLine(item) || pricing.isNativeBundleLine(item)) return;
        const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
        if (!row) return;
        const priceEl = row.querySelector('[data-cart-line-price]');
        const compareEl = row.querySelector('[data-cart-line-compare]');
        const currentEl = row.querySelector('[data-cart-line-current]');
        if (!currentEl) return;
        const originalNet = lineOriginalNet(item, row);
        if (originalNet > 0) currentEl.textContent = pricing.formatNet(originalNet);
        if (compareEl) compareEl.hidden = true;
        priceEl?.classList.remove('is-promo-discounted');
      });
      return;
    }
    if (promoSavings <= 0) return;

    const hasCodePromo = Boolean(promoCodeApplication(cart, drawer));

    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item) || pricing.isNativeBundleLine(item)) return;

      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      if (!row) return;
      const priceEl = row.querySelector('[data-cart-line-price]');
      const compareEl = row.querySelector('[data-cart-line-compare]');
      const currentEl = row.querySelector('[data-cart-line-current]');
      if (!priceEl || !currentEl) return;

      const allocNet = (item.line_level_discount_allocations || []).reduce(
        (sum, alloc) => sum + (Number(alloc.amount) || 0),
        0
      );

      if (hasCodePromo && allocNet > 0) {
        const originalNet = lineOriginalNet(item, row);
        const finalNet = Number(item.final_line_price) || Math.max(0, originalNet - allocNet);
        showPromoLinePrice(priceEl, compareEl, currentEl, pricing, originalNet, finalNet);
        return;
      }

      if (hasCodePromo && promoSavings > 0 && qualifyingNet > 0) {
        const { promoUnits } = bundleAndPromoUnits(item, stackState);
        if (promoUnits > 0) {
          const qty = Number(item.quantity) || 1;
          const originalNet = lineOriginalNet(item, row);
          const unitOriginalNet = Math.round(originalNet / qty);
          const promoLineNet = unitOriginalNet * promoUnits;
          const lineShare = Math.round((promoSavings * promoLineNet) / qualifyingNet);
          if (lineShare > 0) {
            const finalNet = Math.max(0, promoLineNet - lineShare);
            showPromoLinePrice(priceEl, compareEl, currentEl, pricing, promoLineNet, finalNet);
            return;
          }
        }
      }

      if (qualifyingNet <= 0) return;

      const { bundleUnits, promoUnits, qty } = bundleAndPromoUnits(item, stackState);
      if (promoUnits <= 0) return;

      const unitNet = lineUnitNet(item, row, pricing);
      const unitDisplay = pricing.displayCents(unitNet);
      const promoLineNet = unitNet * promoUnits;
      const lineShare = Math.round((promoSavings * promoLineNet) / qualifyingNet);
      const promoLineDisplay = pricing.displayCents(Math.max(0, promoLineNet - lineShare));

      let bundlePartDisplay = 0;
      if (bundleUnits > 0 && stackState?.qualified && stackState?.pct > 0) {
        const discountedUnit = pricing.applyDiscountDisplay(unitDisplay, stackState.pct);
        bundlePartDisplay = discountedUnit * bundleUnits;
      } else if (bundleUnits > 0) {
        bundlePartDisplay = unitDisplay * bundleUnits;
      }

      const currentDisplay = bundlePartDisplay + promoLineDisplay;
      const compareDisplay = unitDisplay * qty;

      if (lineShare > 0 && currentDisplay < compareDisplay) {
        currentEl.textContent = moneyDisplay(currentDisplay);
        if (compareEl) {
          compareEl.textContent = moneyDisplay(compareDisplay);
          compareEl.hidden = false;
        }
        priceEl.classList.add('is-promo-discounted');
      }
    });
  };

  const hideLiquidDiscountRows = (drawer) => {
    drawer?.querySelectorAll('[data-cart-liquid-discount]').forEach((el) => {
      el.hidden = true;
    });
  };

  const estimateShippingNet = (itemsNet, summary) => {
    if (!summary) return 0;
    const threshold = Number(summary.dataset.shippingThresholdCents) || 60000;
    const flat = Number(summary.dataset.shippingFlatCents) || 2500;
    return itemsNet >= threshold ? 0 : flat;
  };

  const shippingNetCents = (drawer, itemsNetAfterDiscounts = 0) => {
    const summary = drawer?.querySelector('[data-cart-summary]');
    if (itemsNetAfterDiscounts > 0 && summary) {
      return estimateShippingNet(itemsNetAfterDiscounts, summary);
    }
    if (summary?.dataset.shippingNetCents != null && summary.dataset.shippingNetCents !== '') {
      return Number(summary.dataset.shippingNetCents) || 0;
    }
    return 0;
  };

  const applySummary = (cart, stackState, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return;

    hideLiquidDiscountRows(drawer);

    const cfg = readThemeConfig(drawer);
    const globalRow = drawer.querySelector('[data-cart-global-discount-row]');
    const globalLabel = drawer.querySelector('[data-cart-global-discount-label]');
    const globalAmount = drawer.querySelector('[data-cart-global-discount-amount]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const promoRow = drawer.querySelector('[data-cart-promo-code-row]');
    const promoLabel = drawer.querySelector('[data-cart-promo-code-label]');
    const promoAmount = drawer.querySelector('[data-cart-promo-code-amount]');
    const subtotalEl = drawer.querySelector('[data-cart-summary-subtotal]');
    const shippingEl = drawer.querySelector('[data-cart-summary-shipping]');
    const vatEl = drawer.querySelector('[data-cart-summary-vat]');
    const totalEl = drawer.querySelector('[data-cart-summary-total]');

    const netSubtotal = stackState?.netSubtotalCents || 0;
    const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
    const itemsNet = catalogNet > 0 ? catalogNet : netSubtotal;
    const bundleSavings = stackState?.bundleSavingsNetCents || 0;
    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    const promo = computePromoSavings(cart, drawer, stackState, cfg);
    const shopifyPromoNet = linePromoAllocationNet(cart);
    const shopifyCartDiscount = Number(cart?.total_discount) || 0;
    const shopifyPromoDeduction = capPromoSavings(
      shopifyPromoNet > 0 ? shopifyPromoNet : shopifyCartDiscount,
      drawer
    );
    const promoDisplaySavings =
      shopifyPromoDeduction > 0
        ? shopifyPromoDeduction
        : promo.savings > 0
          ? promo.savings
          : 0;
    const promoLabelText =
      promo.label || promoTitleFromLines(cart) || storedPromoCode() || '';

    if (globalRow) globalRow.hidden = true;

    if (promoRow && promoLabel && promoAmount) {
      if (promoDisplaySavings > 0 && promoLabelText) {
        promoRow.hidden = false;
        const codeLabel = String(promoLabelText).toUpperCase();
        promoLabel.textContent =
          promoLabel.dataset.codeTemplate?.replace('__TITLE__', codeLabel) || codeLabel;
        promoAmount.textContent = `-${moneyNet(promoDisplaySavings)}`;
      } else {
        promoRow.hidden = true;
        promoLabel.textContent = '';
        promoAmount.textContent = '';
      }
    }

    applyPromoLinePrices(cart, drawer, stackState, promoDisplaySavings, qualifyingNet);

    if (itemsNet <= 0) return;

    const promoVisible = Boolean(promoRow && !promoRow.hidden && promoDisplaySavings > 0);
    const promoDeduction = promoVisible ? promoDisplaySavings : 0;
    const netAfterDiscounts = Math.max(0, itemsNet - bundleSavings - promoDeduction);
    const shippingNet = shippingNetCents(drawer, netAfterDiscounts);
    const taxableNet = netAfterDiscounts + shippingNet;
    const vatCents = pricing.vatOnNet(taxableNet);
    const adjustedTotal = taxableNet + vatCents;

    if (subtotalEl) subtotalEl.textContent = moneyNet(itemsNet);
    if (summary) summary.dataset.shippingNetCents = String(shippingNet);
    if (shippingEl) {
      shippingEl.textContent =
        shippingNet === 0
          ? shippingEl.dataset.freeLabel || 'Free'
          : moneyNet(shippingNet);
    }
    if (vatEl) vatEl.textContent = moneyNet(vatCents);
    if (totalEl) totalEl.textContent = moneyNet(adjustedTotal);
  };

  const bind = () => {
    document.addEventListener('ghalia:cart:stack-discount', (e) => {
      const drawer = document.querySelector('[data-cart-drawer]');
      applySummary(e.detail?.cart, e.detail?.state, drawer);
    });
    document.addEventListener('ghalia:cart-drawer:refreshed', () => {
      hideLiquidDiscountRows(document.querySelector('[data-cart-drawer]'));
    });
  };

  window.GhaliaCartSummary = { apply: applySummary };

  window.GhaliaPromo = {
    meetsMinimum: qualifyingMeetsPromoMinimum,
    shopifyDiscountNet: (cart) => {
      const fromLines = linePromoAllocationNet(cart);
      if (fromLines > 0) return fromLines;
      return Number(cart?.total_discount) || 0;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
